import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useInstructorDashboard } from '@/hooks/useInstructorDashboard'
import type { DashboardInvitation, DashboardLesson } from '@/hooks/useInstructorDashboard'
import { Badge, type BadgeTone } from '@/components/ui/Badge'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Icon, ICONS, type IconName } from '@/components/ui/Icon'

const LESSON_STATUS_COLOR: Record<DashboardLesson['status'], string> = {
  scheduled: '#0F7C6C',
  completed: '#1B2A4A',
  cancelled: 'var(--danger)',
}

// ── Status badge ─────────────────────────────────────────────────────────────

const STATUS: Record<DashboardInvitation['status'], { tone: BadgeTone; label: string }> = {
  pending:  { tone: 'warning', label: 'Pending' },
  accepted: { tone: 'success', label: 'Accepted' },
  revoked:  { tone: 'danger',  label: 'Revoked' },
  expired:  { tone: 'neutral', label: 'Expired' },
}

function StatusBadge({ status }: { status: DashboardInvitation['status'] }) {
  const s = STATUS[status]
  return <Badge label={s.label} tone={s.tone} />
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  value, label, action, onAction,
}: {
  value: number | string
  label: string
  action?: string
  onAction?: () => void
}) {
  return (
    <div style={{
      background: 'linear-gradient(155deg, #0F7C6C, #0B5D51)', borderRadius: 14, padding: '20px 22px',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ font: '700 34px Lora, serif', color: '#fff', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ font: 'var(--text-body)', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
        {label}
      </div>
      {action && onAction && (
        <button
          onClick={onAction}
          style={{
            marginTop: 10, padding: 0, background: 'none', border: 'none',
            font: '500 12.5px Inter, sans-serif', color: '#fff',
            cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 3,
          }}
        >
          {action} →
        </button>
      )}
    </div>
  )
}

// ── Quick action card ─────────────────────────────────────────────────────────

// A distinct brand gradient per quick action -- not just one repeated teal, so the three
// cards read as different things to do, not three copies of the same card.
const ACTION_GRADIENTS = {
  teal:  'linear-gradient(155deg, #0F7C6C, #0B5D51)',
  amber: 'linear-gradient(155deg, #D4972B, #9A6C0E)',
  navy:  'linear-gradient(155deg, #1B2A4A, #0F3D52)',
} as const

function ActionCard({
  icon, label, description, onClick, gradient,
}: {
  icon: IconName
  label: string
  description: string
  onClick: () => void
  gradient: keyof typeof ACTION_GRADIENTS
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
        borderRadius: 14, padding: '18px 20px', textAlign: 'left', overflow: 'hidden',
        cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        width: '100%',
      }}
      onMouseEnter={e => {
        const el = e.currentTarget
        el.style.borderColor = 'rgba(27,42,74,0.15)'
        el.style.boxShadow   = '0 8px 20px rgba(27,42,74,0.1)'
        el.style.transform   = 'translateY(-2px)'
      }}
      onMouseLeave={e => {
        const el = e.currentTarget
        el.style.borderColor = 'rgba(27,42,74,0.08)'
        el.style.boxShadow   = 'none'
        el.style.transform   = 'none'
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: ACTION_GRADIENTS[gradient],
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginBottom: 12,
      }}>
        <Icon path={ICONS[icon]} size={18} />
      </div>
      <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
        {description}
      </div>
    </button>
  )
}

// ── Format helpers ─────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Dashboard skeleton ────────────────────────────────────────────────────────

function Skeleton({ w = '100%', h = 18, radius = 8, light = false }: { w?: string | number; h?: number; radius?: number; light?: boolean }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: radius,
      background: light ? 'rgba(255,255,255,0.18)' : 'rgba(27,42,74,0.07)',
      animation: 'pulse 1.4s ease-in-out infinite',
    }} />
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function InstructorDashboard() {
  const navigate  = useNavigate()
  const { profile, instructorProfile } = useAuth()
  const { data, loading, error, refresh } = useInstructorDashboard()

  const displayName = profile?.full_name ?? 'Instructor'
  const serviceArea = instructorProfile?.service_area ?? null

  return (
    <div>
      {/* Keyframe for skeleton pulse — injected once */}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }`}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 4 }}>
          Welcome back, {displayName}
        </div>
        {serviceArea && (
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            {serviceArea}
          </div>
        )}
      </div>

      {/* ── Error state ── */}
      {error && !loading && (
        <div style={{ marginBottom: 24 }}>
          <div className='banner-error' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
            <span>{error}</span>
            <button
              onClick={refresh}
              style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', flexShrink: 0 }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* ── Stats row ── */}
      <div className='g3' style={{ marginBottom: 22 }}>
        {loading ? (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ background: 'linear-gradient(155deg, #0F7C6C, #0B5D51)', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton h={36} w={60} radius={6} light />
                <Skeleton h={14} w='55%' light />
              </div>
            ))}
          </>
        ) : (
          <>
            <StatCard
              value={data?.activeStudentCount ?? 0}
              label='Active students'
              action={data?.activeStudentCount ? 'View all students' : undefined}
              onAction={() => navigate('/app/instructor/students')}
            />
            <StatCard
              value={data?.pendingInviteCount ?? 0}
              label='Pending invitations'
              action={data?.pendingInviteCount ? 'Manage invites' : undefined}
              onAction={() => navigate('/app/instructor/students', { state: { tab: 'invites', openForm: true } })}
            />
            <StatCard
              value={data?.lessonsLoggedLast30Days ?? 0}
              label='Lessons logged (30 days)'
            />
          </>
        )}
      </div>

      <div className='dash-layout'>
      <div>

      {/* ── Recent lessons ── */}
      <div style={{ marginBottom: 22 }}>
        <SectionHeader>Recent lessons</SectionHeader>
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Skeleton w='45%' h={14} />
                  <Skeleton w={80} h={12} />
                </div>
              ))}
            </div>
          ) : (data?.recentLessons.length ?? 0) === 0 ? (
            <div style={{ padding: '36px 28px', textAlign: 'center' }}>
              <div style={{ font: '600 15px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>No lessons logged yet</div>
              <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
                Log a lesson from a student's page to see it here.
              </div>
            </div>
          ) : (
            data!.recentLessons.map((l, i) => {
              const isLast = i === data!.recentLessons.length - 1
              return (
                <div key={l.id} style={{
                  padding: '13px 20px', borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                      {l.studentName ?? 'Student'}
                    </div>
                    <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginTop: 2 }}>
                      {formatDate(l.scheduledAt)}{l.durationMinutes != null ? ` · ${l.durationMinutes} min` : ' · in progress'}
                    </div>
                  </div>
                  <span style={{
                    font: '600 11.5px Inter, sans-serif', color: LESSON_STATUS_COLOR[l.status],
                    textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
                  }}>
                    {l.status}
                  </span>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div style={{ marginBottom: 22 }}>
        <SectionHeader>Quick actions</SectionHeader>
        <div className='g3'>
          <ActionCard
            icon='plus'
            gradient='teal'
            label='Enroll a student'
            description='Generate an invite code and share it with a new student'
            onClick={() => navigate('/app/instructor/students', { state: { tab: 'invites', openForm: true } })}
          />
          <ActionCard
            icon='users'
            gradient='amber'
            label='View students'
            description='See your active students and their progress'
            onClick={() => navigate('/app/instructor/students')}
          />
          <ActionCard
            icon='gear'
            gradient='navy'
            label='Settings'
            description='Update your profile, phone, and password'
            onClick={() => navigate('/app/instructor/settings')}
          />
        </div>
      </div>

      </div>
      <div>

      {/* ── Needs attention ── */}
      {!loading && !error && (data?.needsAttention.length ?? 0) > 0 && (
        <div style={{ marginBottom: 22 }}>
          <SectionHeader>Needs attention</SectionHeader>
          <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderLeft: '3px solid var(--amber)', borderRadius: 14, overflow: 'hidden' }}>
            {data!.needsAttention.map((s, i) => {
              const isLast = i === data!.needsAttention.length - 1
              const reasons: string[] = []
              if (s.g1Reason === 'g1_expired') reasons.push('G1 expired')
              if (s.g1Reason === 'g1_expiring_soon') reasons.push('G1 expiring soon')
              if (s.needsPracticeCount > 0) reasons.push(`${s.needsPracticeCount} skill${s.needsPracticeCount === 1 ? '' : 's'} need practice`)
              return (
                <div key={s.studentId} style={{
                  padding: '13px 20px', borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                }}>
                  <div>
                    <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{s.fullName ?? s.email}</div>
                    <div style={{ font: '400 12px Inter, sans-serif', color: '#9A6C0E', marginTop: 2 }}>{reasons.join(' · ')}</div>
                  </div>
                  <button
                    onClick={() => navigate(`/app/instructor/students`)}
                    style={{ background: 'rgba(27,42,74,0.06)', border: 'none', borderRadius: 7, padding: '6px 14px', font: '500 12.5px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer', flexShrink: 0 }}
                  >
                    View students
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Recent invitations ── */}
      <div>
        <SectionHeader
          action={!loading && (data?.recentInvitations.length ?? 0) > 0 && (
            <button
              onClick={() => navigate('/app/instructor/students', { state: { tab: 'invites', openForm: true } })}
              style={{ background: 'none', border: 'none', font: '500 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
            >
              View all →
            </button>
          )}
        >
          Recent invitations
        </SectionHeader>

        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Skeleton w='45%' h={14} />
                  <Skeleton w={60} h={22} radius={20} />
                  <Skeleton w={80} h={12} />
                </div>
              ))}
            </div>
          ) : (data?.recentInvitations.length ?? 0) === 0 ? (
            /* Empty state */
            <div style={{ padding: '36px 28px', textAlign: 'center' }}>
              <div style={{ font: '600 15px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
                No invitations yet
              </div>
              <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 20 }}>
                {(data?.activeStudentCount ?? 0) === 0
                  ? 'Enroll your first student to get started.'
                  : 'All invitations have been accepted or have expired.'}
              </div>
              <button
                onClick={() => navigate('/app/instructor/students', { state: { tab: 'invites', openForm: true } })}
                className='btn-teal'
                style={{ width: 'auto', padding: '11px 22px' }}
              >
                Enroll a student
              </button>
            </div>
          ) : (
            /* Invitation rows */
            data!.recentInvitations.map((inv, i) => {
              const isLast = i === data!.recentInvitations.length - 1
              return (
                <div key={inv.id} style={{
                  padding: '13px 20px',
                  borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inv.invited_email ?? '—'}
                    </div>
                    <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginTop: 2 }}>
                      {inv.accepted_at
                        ? `Accepted ${formatDate(inv.accepted_at)}`
                        : `Sent ${formatDate(inv.created_at)}`}
                    </div>
                  </div>
                  <StatusBadge status={inv.status} />
                </div>
              )
            })
          )}
        </div>

        {/* Empty student state — shown when loaded, no students, and no invitations */}
        {!loading && !error && (data?.activeStudentCount ?? 0) === 0 && (data?.recentInvitations.length ?? 0) === 0 && (
          <div style={{ marginTop: 18 }} className='banner-info'>
            You don't have any active students yet. Use <strong>Enroll a student</strong> above to get started.
          </div>
        )}
      </div>

      </div>
      </div>
    </div>
  )
}
